import { type ChatUpgradeCardKind, getChatUpgradeCardType } from '@/lib/chat/upgrade-card';

// Stable analytics identifiers live beside the copy so edits to a heading don't change attribution.
export const CHAT_UPGRADE_MODALS = {
  upgrade: { type: 'upgrade_plan', title: 'Upgrade your plan' },
  intelligence: { type: 'smarter_models', title: 'Unlock smarter models' },
  reasoning: { type: 'more_reasoning', title: 'Unlock more reasoning' },
  model_picker: { type: 'stronger_models', title: 'Unlock stronger models' },
  model_picker_menu: { type: 'stronger_models', title: 'Unlock stronger models' },
  model_picker_inline: { type: 'stronger_models', title: 'Unlock stronger models' },
  more_credits: { type: 'more_credits', title: 'Add more credits' },
  credits_usage: { type: 'more_credits', title: 'Add more credits' },
  more_credits_short: { type: 'more_credits', title: 'Add more credits' },
  credits: { type: 'more_credits', title: 'Add more credits' },
  credits_upgrade: { type: 'credits_and_models', title: 'Unlock more credits and models' },
  credits_paused: { type: 'more_credits', title: 'Add more credits' },
  offer_banner: { type: 'pro_offer', title: 'Upgrade to Pro' }
} as const satisfies Record<ChatUpgradeCardKind, { type: string; title: string }>;

export type UpgradeModalSource = {
  cardName: ChatUpgradeCardKind;
  turnPhase?: 'mid_turn' | 'post_turn';
  userType?: 'free' | 'paid';
  plan?: string;
  copy?: string;
};

// modalProps is shared by many dialogs, so ignore unrelated/invalid payloads.
export function getUpgradeModalSource(value: unknown): UpgradeModalSource | undefined {
  if (!value || typeof value !== 'object' || !('cardName' in value)) return;
  if (typeof value.cardName !== 'string' || !Object.hasOwn(CHAT_UPGRADE_MODALS, value.cardName))
    return;

  return {
    cardName: value.cardName as ChatUpgradeCardKind,
    turnPhase:
      'turnPhase' in value && (value.turnPhase === 'mid_turn' || value.turnPhase === 'post_turn')
        ? value.turnPhase
        : undefined,
    userType:
      'userType' in value && (value.userType === 'free' || value.userType === 'paid')
        ? value.userType
        : undefined,
    plan: 'plan' in value && typeof value.plan === 'string' ? value.plan : undefined,
    copy: 'copy' in value && typeof value.copy === 'string' ? value.copy : undefined
  };
}

export function getUpgradeModalAttribution(source?: UpgradeModalSource, isPaused = false) {
  return {
    modal_type: isPaused
      ? 'paused_subscription'
      : source
        ? CHAT_UPGRADE_MODALS[source.cardName].type
        : 'default',
    modal_source: source ? 'chat_upgrade_card' : 'default',
    ...(source
      ? {
          source_card_name: source.cardName,
          source_card_type: getChatUpgradeCardType(source.cardName),
          source_turn_phase: source.turnPhase,
          source_user_type: source.userType,
          source_plan: source.plan,
          ...(source.copy ? { source_copy: source.copy } : {})
        }
      : {})
  };
}
